import { useState } from "react";
import { KeyRound, Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, errDetail, errMsg } from "@/lib/api";
import { Panel, Tag, Spinner } from "@/components/common";

const FIELDS = [
  { key: "client_id", label: "OAuth Client ID", placeholder: "xxxxxxxx.apps.googleusercontent.com" },
  { key: "client_secret", label: "OAuth Client Secret", placeholder: "GOCSPX-…" },
  { key: "login_customer_id", label: "Manager (Login) Customer ID", placeholder: "123-456-7890 or 1234567890" },
  { key: "developer_token", label: "Developer Token (optional · sunset by Google)", placeholder: "Not needed since 9 Sep 2026; leave empty" },
];

const SourceTag = ({ cred, testid }) => {
  if (!cred.set) return cred.required ? <Tag tone="red" testid={testid}>Missing</Tag> : <Tag testid={testid}>Optional</Tag>;
  return <Tag tone={cred.source === "env" ? "cyan" : "emerald"} testid={testid}>{cred.source === "env" ? "From .env ✓" : "Saved ✓"}</Tag>;
};

export default function CredentialsForm({ status, onSaved }) {
  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [busy, setBusy] = useState(false);
  const unsaved = Object.values(values).some((v) => v.trim());

  const save = async (e) => {
    e.preventDefault();
    const body = Object.fromEntries(Object.entries(values).filter(([, v]) => v.trim()));
    if (!Object.keys(body).length) return setFormError("Paste at least one value, then save.");
    setBusy(true);
    setErrors({});
    setFormError("");
    try {
      const { data } = await api.put("/google-ads/credentials", body);
      setValues({});
      toast.success(`Saved: ${data.saved_fields.join(", ")}`);
      onSaved();
    } catch (err) {
      const d = errDetail(err);
      setErrors(d?.errors || {});
      setFormError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const clear = async (key) => {
    if (!window.confirm(`Remove the saved ${key.replace(/_/g, " ")}?`)) return;
    try {
      await api.delete(`/google-ads/credentials/${key}`);
      toast.success("Stored value removed");
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <Panel title="API credentials" icon={KeyRound} testid="credentials-panel">
      <p className="mb-6 text-sm text-slate-400">
        Paste the values, then press <b className="text-slate-200">Save credentials</b>. They are encrypted in the database and override <span className="font-mono text-slate-300">backend/.env</span>. Spaces, dashes in the customer ID and line breaks are cleaned automatically.
      </p>
      <form onSubmit={save} className="space-y-5" data-testid="credentials-form">
        {FIELDS.map(({ key, label, placeholder }) => {
          const cred = status.credentials[key];
          return (
            <div key={key}>
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-slate-200">{label}</span>
                  <span className="font-mono text-[10px] text-slate-600">{cred.env_name}</span>
                </div>
                <div className="flex items-center gap-2">
                  {cred.masked && <span data-testid={`credential-${key}-masked`} className="font-mono text-xs text-slate-400">{cred.masked}</span>}
                  <SourceTag cred={cred} testid={`credential-${key}-source`} />
                  {cred.source === "database" && (
                    <button type="button" data-testid={`credential-${key}-clear-button`} onClick={() => clear(key)} className="text-slate-500 transition-colors hover:text-red-300" title="Remove stored value">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              </div>
              <input
                data-testid={`credential-${key}-input`}
                type={key === "login_customer_id" ? "text" : "password"}
                autoComplete="off"
                className={`cc-input font-mono ${errors[key] ? "!border-red-500/70" : ""}`}
                placeholder={cred.set ? "Saved. Enter a new value to replace it" : placeholder}
                value={values[key] || ""}
                onChange={(e) => { setValues((v) => ({ ...v, [key]: e.target.value })); setErrors((er) => ({ ...er, [key]: undefined })); }}
              />
              {errors[key] && <div data-testid={`credential-${key}-error`} className="mt-1.5 text-xs text-red-300">{errors[key]}</div>}
            </div>
          );
        })}
        {formError && <div data-testid="credentials-save-error" className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">{formError}</div>}
        {unsaved && (
          <div data-testid="credentials-unsaved-warning" className="flex items-center gap-2 text-xs text-amber-300">
            <AlertTriangle className="h-3.5 w-3.5" /> Unsaved values. Save them before pressing Connect.
          </div>
        )}
        <button data-testid="credentials-save-button" type="submit" disabled={busy} className="cc-btn cc-btn-primary">
          {busy && <Spinner className="h-4 w-4 text-[#03130c]" />} Save credentials
        </button>
      </form>
    </Panel>
  );
}
